/**
 * Finnish display metadata for the Kehitys board.
 *
 * Same split as lib/taskStatusMeta.ts: the enum keys are English and live in the
 * database, the labels here are what a user reads.
 *
 * Terminology note — the vote is an "ääni" and the verb is "äänestä". Never
 * "kannatus"/"kannata".
 */

import { BugIcon, IdeaIcon, type LucideIcon } from "@/lib/icons";
import type { FeedbackKind, FeedbackStatus } from "@/types";

export interface FeedbackStatusMeta {
  label: string;
  /** Tailwind classes for the status pill. */
  pill: string;
}

export const FEEDBACK_STATUS_META: Record<FeedbackStatus, FeedbackStatusMeta> = {
  NEW: {
    label: "Uusi",
    pill: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  TRIAGED: {
    label: "Käsittelyssä",
    pill: "bg-primary/10 text-primary",
  },
  IN_PROGRESS: {
    label: "Työn alla",
    pill: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  },
  DONE: {
    label: "Valmis",
    pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  },
  WONT_DO: {
    label: "Ei toteuteta",
    pill: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  },
};

export interface FeedbackKindMeta {
  label: string;
  /** Short helper shown under the label in the create dialog. */
  hint: string;
  icon: LucideIcon;
  iconClass: string;
}

export const FEEDBACK_KIND_META: Record<FeedbackKind, FeedbackKindMeta> = {
  BUG: {
    label: "Vika",
    hint: "Jokin ei toimi niin kuin pitäisi",
    icon: BugIcon,
    iconClass: "text-destructive",
  },
  IDEA: {
    label: "Kehitysidea",
    hint: "Parannus tai puuttuva toiminto",
    icon: IdeaIcon,
    iconClass: "text-primary",
  },
};

/** Sort keys accepted by the feedbackReports query, with their labels. */
export const FEEDBACK_SORT_OPTIONS = [
  { value: "votes", label: "Eniten ääniä" },
  { value: "newest", label: "Uusimmat" },
  { value: "discussed", label: "Keskustelluimmat" },
] as const;

export type FeedbackSort = (typeof FEEDBACK_SORT_OPTIONS)[number]["value"];

/**
 * "0:34" / "1:05". Used on the play badge over a video's poster frame — it is
 * what tells someone this is a clip, and how long, before they load it.
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "";
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** Status filter options. An empty value means "open reports", the default. */
export const FEEDBACK_STATUS_FILTERS = [
  { value: "", label: "Avoimet" },
  { value: "ALL", label: "Kaikki tilat" },
  { value: "NEW", label: "Uusi" },
  { value: "TRIAGED", label: "Käsittelyssä" },
  { value: "IN_PROGRESS", label: "Työn alla" },
  { value: "DONE", label: "Valmis" },
  { value: "WONT_DO", label: "Ei toteuteta" },
] as const;
